/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { accessAPI } from "../../../shared";
import { Formulario } from "../../../shared";
import { Skeleton } from "primereact/skeleton";
import { Toast } from "primereact/toast";

export default function FormularioProducto({
  proceso,
  pasoId,
  agregarProductoAProceso,
  actualizarProductoEnLista,
  modoEdicion,
  productoSeleccionado,
  formularioKey,
}) {
  const [loaderAgregarProducto, setLoaderAgregarProducto] = useState(false);
  const [loaderFormulario, setLoaderFormulario] = useState(true);
  const [formularioProductoConfig, setFormularioProductoConfig] = useState();

  const toast = useRef(null);

  // Cuando carga el proceso y los tipos de pasos apaga el loader
  useEffect(() => {
    // Función ejecutada al agregar un producto
    function submitAgregarProducto(contenidoCampos) {
      // Prende el loader del formulario
      setLoaderAgregarProducto(true);

      let camposPropios = {};
      let caracteristicasCriticas = {};

      // Itera el objeto buscando campos propios y características críticas
      for (const key in contenidoCampos) {
        if (key !== "marca" && key !== "modelo" && key !== "descripcion") {
          // Si el key empieza con 'cc_' es una característica crítica
          if (key.startsWith("cc_")) {
            const caracteristicaId = key.replace("cc_", "");
            caracteristicasCriticas[caracteristicaId] = contenidoCampos[key];
          } else {
            camposPropios[key] = contenidoCampos[key];
          }
        }
      }

      const camposParaMandar = {
        campospropios: camposPropios,
        caracteristicascriticas: caracteristicasCriticas,
      };
      accessAPI(
        "POST",
        `colab/actividad/${pasoId}/producto`,
        camposParaMandar,
        (respuesta) => {
          setLoaderAgregarProducto(false);
          agregarProductoAProceso(respuesta);
          toast.current.show({
            severity: "success",
            summary: "Éxito",
            detail: "Producto agregado exitosamente",
            life: 3000,
          });
        },
        (respuesta) => {
          // Le avisa al usuario
          setLoaderAgregarProducto(false);
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail:
              respuesta[0].msg ||
              "Ocurrió un error, intente nuevamente más tarde",
            sticky: true,
          });
        },
      );
    }

    // Función ejecutada al editar un producto
    function submitEditarProducto(contenidoCampos) {
      setLoaderAgregarProducto(true);

      let camposPropios = {};
      let caracteristicasCriticas = {};

      // Itera el objeto buscando campos propios y características críticas
      for (const key in contenidoCampos) {
        if (key !== "marca" && key !== "modelo" && key !== "descripcion") {
          // Si el key empieza con 'cc_' es una característica crítica
          if (key.startsWith("cc_")) {
            const caracteristicaId = key.replace("cc_", "");
            caracteristicasCriticas[caracteristicaId] = contenidoCampos[key];
          } else {
            camposPropios[key] = contenidoCampos[key];
          }
        }
      }

      const camposParaMandar = {
        campospropios: camposPropios,
        caracteristicascriticas: caracteristicasCriticas,
      };

      accessAPI(
        "PUT",
        `colab/actividad/${pasoId}/producto/${productoSeleccionado.id}`,
        camposParaMandar,
        (respuesta) => {
          setLoaderAgregarProducto(false);
          actualizarProductoEnLista(respuesta);
          toast.current.show({
            severity: "success",
            summary: "Éxito",
            detail: "Producto actualizado exitosamente",
            life: 3000,
          });
        },
        (respuesta) => {
          setLoaderAgregarProducto(false);
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail:
              respuesta[0].msg ||
              "Ocurrió un error, intente nuevamente más tarde",
            sticky: true,
          });
        },
      );
    }

    const formAgregarProductoConfig = {
      secciones: [],
      botones: [
        {
          etiqueta: modoEdicion ? "Actualizar producto" : "Agregar producto",
          clases: "verde",
          callback: modoEdicion ? submitEditarProducto : submitAgregarProducto,
          validar: true,
        },
      ],
    };
    let formularioProductoConfigParaModificar = {
      ...formAgregarProductoConfig,
    };

    // Agregar sección de características críticas
    if (
      proceso.certificadotipo?.ProductoCaracteristicasCriticas &&
      proceso.certificadotipo.ProductoCaracteristicasCriticas.length > 0
    ) {
      let seccionCaracteristicasCriticas = {
        nombre: "Características críticas",
        campos: [],
      };

      for (const caracteristicaCritica of proceso.certificadotipo
        ?.ProductoCaracteristicasCriticas) {
        let campoParaConfigurar = {};
        // Usar prefijo 'cc_' para identificar características críticas
        campoParaConfigurar.nombre = `cc_${caracteristicaCritica.id}`;
        campoParaConfigurar.etiqueta = caracteristicaCritica.etiqueta;
        campoParaConfigurar.notas = caracteristicaCritica.notas;
        campoParaConfigurar.tipoitemformularioid = 1; // Campo de texto
        campoParaConfigurar.requerido = false;

        // Si estamos en modo edición o duplicación, popular el campo con el valor del producto
        if (productoSeleccionado) {
          const caracteristicaEnProducto =
            productoSeleccionado.producto_x_productocaracteristicascriticas?.find(
              (cc) => cc.caracteristicacritica?.id === caracteristicaCritica.id,
            );
          if (caracteristicaEnProducto) {
            campoParaConfigurar.respuesta = caracteristicaEnProducto.valor;
          }
        }

        seccionCaracteristicasCriticas.campos.push(campoParaConfigurar);
      }

      formularioProductoConfigParaModificar.secciones.push(
        seccionCaracteristicasCriticas,
      );
    }

    // Agregar sección de campos propios del producto
    if (
      proceso.certificadotipo?.productocampospropios &&
      proceso.certificadotipo.productocampospropios.length > 0
    ) {
      let seccionCamposPropios = {
        nombre: "Campos del producto",
        campos: [],
      };

      for (const campoPropio of proceso.certificadotipo
        ?.productocampospropios) {
        // Genera un objeto para formatear la configuración según la API de formulario
        let campoParaConfigurar = {};
        campoParaConfigurar.nombre = campoPropio.id;
        campoParaConfigurar.etiqueta = campoPropio.unidades
          ? `${campoPropio.etiqueta} (${campoPropio.unidades})`
          : campoPropio.etiqueta;
        campoParaConfigurar.notas = campoPropio.notas;
        switch (campoPropio.tipo) {
          case "int":
            campoParaConfigurar.tipoitemformularioid = 4;
            break;
          case "float":
            campoParaConfigurar.tipoitemformularioid = 8;
            break;
          default:
            campoParaConfigurar.tipoitemformularioid = 1;
            break;
        }
        campoParaConfigurar.requerido = campoPropio.obligatoriobool;
        // Si tiene opciones las carga
        if (campoPropio.opciones?.length) {
          campoParaConfigurar.opciones = campoPropio.opciones.map((opcion) => {
            return { value: opcion, etiqueta: opcion };
          });
          campoParaConfigurar.sinOpcionInicial = true;
          campoParaConfigurar.tipoitemformularioid = 5;
        }
        // Si tiene un script para autocompletar, deshabilita el campo y le avisa al usuario
        if (campoPropio.autocompletarScript) {
          campoParaConfigurar.disabled = true;
          // Texto, aunque el campo sea numérico: el cartel es un string y un
          // InputNumber lo muestra como NaN. El tipo guardado ya se fuerza a
          // string al cargar el script, pero se puede volver a cambiar desde
          // la edición del campo, así que acá no se depende de eso.
          campoParaConfigurar.tipoitemformularioid = 1;
          campoParaConfigurar.respuesta = "Autocompletado por script";
        }

        // Si estamos en modo edición o duplicación, popular el campo con el valor del producto
        if (productoSeleccionado && !campoPropio.autocompletarScript) {
          const campoEnProducto =
            productoSeleccionado.producto_x_productocampospropios?.find(
              (cp) => cp.productocampospropios?.id === campoPropio.id,
            );
          if (campoEnProducto) {
            campoParaConfigurar.respuesta = campoEnProducto.valor;
          }
        }

        seccionCamposPropios.campos.push(campoParaConfigurar);
      }

      formularioProductoConfigParaModificar.secciones.push(
        seccionCamposPropios,
      );
    }

    setFormularioProductoConfig({ ...formularioProductoConfigParaModificar });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modoEdicion, productoSeleccionado]);

  // Cuando termina de configurar el formulario, apaga el loader
  useEffect(() => {
    if (formularioProductoConfig) {
      setLoaderFormulario(false);
    }
  }, [formularioProductoConfig]);

  return (
    <>
      <Toast ref={toast} position="top-center" />
      {proceso && (
        <div>
          <h1>{modoEdicion ? "Editar producto" : "Agregar producto"}</h1>
          {loaderFormulario && (
            <div
              css={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                marginTop: "4em",
              }}
            >
              {[1, 2, 3, 4].map((i) => (
                <div key={i} css={{ marginBottom: "1em" }}>
                  <Skeleton
                    width="8rem"
                    height="1rem"
                    css={{ marginBottom: "1em" }}
                  />
                  <Skeleton width="100%" height="2.5rem" />
                </div>
              ))}
            </div>
          )}
          {!loaderFormulario && (
            <Formulario
              key={`formulario-${formularioKey}-${modoEdicion ? productoSeleccionado?.id : "nuevo"}`}
              config={formularioProductoConfig}
              loader={loaderAgregarProducto}
            />
          )}
        </div>
      )}
    </>
  );
}
